import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { apiFetch } from '@/api/client'
import { paiseToDisplay, parseCurrencyType, type CurrencyType } from '@/lib/currency'
import type {
  CreditLimitTransferResult,
  CreditLimitTransferRow,
} from '@/features/logs/clTransfersTypes'

/** Agent-API `POST /v1/credit-limit-logs/searchCreditLimitLogs` (+ BQ twin) request. */
export type SearchCreditLimitLogsRequest = {
  page?: number
  pageSize?: number
  fromDate?: string
  toDate?: string
}

/** One Lab/Agent-API CL log row (minor-unit money strings). */
export type CreditLimitLogApiRow = {
  id: string
  createdAt: string
  status?: string
  fromUsername: string
  toUsername: string
  creditLimit: string
  balance: string
  oldBalance: string
  difference?: string
  clientIp: string
}

/** Standard API Response from Agent-API hierarchy-service. */
export type SearchCreditLimitLogsResponse = {
  status: number
  message: string
  data: {
    results: CreditLimitLogApiRow[]
    totalCount: number
    page: number
    pageSize: number
  }
}

function searchBody(body: SearchCreditLimitLogsRequest): SearchCreditLimitLogsRequest {
  const out: SearchCreditLimitLogsRequest = {
    page: body.page ?? 1,
    pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  if (body.fromDate) out.fromDate = body.fromDate
  if (body.toDate) out.toDate = body.toDate
  return out
}

/**
 * Lab `CreditLimitLogs/searchCreditLimitLogs` → Agent-API hierarchy (Postgres).
 * Returns the raw envelope; map with `toCreditLimitTransferResult`.
 */
export async function searchCreditLimitLogs(
  body: SearchCreditLimitLogsRequest = {},
): Promise<SearchCreditLimitLogsResponse> {
  return apiFetch<SearchCreditLimitLogsResponse>('/v1/credit-limit-logs/searchCreditLimitLogs', {
    method: 'POST',
    body: JSON.stringify(searchBody(body)),
  })
}

/**
 * CL Transfers historical — BigQuery twin; same request/response as `searchCreditLimitLogs`.
 */
export async function searchCreditLimitLogsBQ(
  body: SearchCreditLimitLogsRequest = {},
): Promise<SearchCreditLimitLogsResponse> {
  return apiFetch<SearchCreditLimitLogsResponse>(
    '/v1/credit-limit-logs/searchCreditLimitLogsBQ',
    {
      method: 'POST',
      body: JSON.stringify(searchBody(body)),
    },
  )
}

/** Map hierarchy minor-unit rows → console display units for CL Transfers. */
export function toCreditLimitTransferResult(
  live: SearchCreditLimitLogsResponse,
  currencyRaw?: string | null,
): CreditLimitTransferResult {
  const ccy: CurrencyType = parseCurrencyType(currencyRaw)
  const toDisplay = (minor: string | number | null | undefined) => paiseToDisplay(minor, ccy)
  const page = live.data?.page ?? 1
  const pageSize = live.data?.pageSize ?? DEFAULT_PAGE_SIZE
  const total = live.data?.totalCount ?? 0
  const rows: CreditLimitTransferRow[] = (live.data?.results ?? []).map((row) => ({
    id: row.id,
    createdAt: row.createdAt,
    fromUsername: row.fromUsername ?? '',
    toUsername: row.toUsername ?? '',
    creditLimit: toDisplay(row.creditLimit),
    balance: toDisplay(row.balance),
    oldBalance: toDisplay(row.oldBalance),
    clientIp: row.clientIp ?? '',
  }))
  return { rows, total, page, pageSize }
}
